import {
  Text,
  Box,
  HStack,
  VStack,
  Flex,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Icon,
  Tooltip,
  Button,
  ModalCloseButton,
  ModalBody,
  ModalOverlay,
  ModalHeader,
  Modal,
  ModalContent,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from "@chakra-ui/react";
import { useEffect } from "react";
import {
  FaCheckCircle,
  FaInfoCircle,
  FaUser,
  FaBuilding,
} from "react-icons/fa";
import { MdEmail, MdWhatsapp, MdMessage, MdRefresh } from "react-icons/md";

import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import {
  getReportProposalSendEmail,
  resendProposalEmailEmployee,
  resendProposalEmailEmployer,
  resendProposalWhatsApp,
} from "@/services/consignado/proposal.service";
import { NotificationReportResponse } from "@/models/consignado/proposal.model";
import { documentFormatter } from "@/utils/documentFormatter";
import { phoneFormatter, formatPhone } from "@/utils/formatPhone";

interface ChannelStatusProps {
  icon: any;
  channelName: string;
  address: string | null;
  enviado: boolean;
  data_envio: string | Date | null;
  erro: string | null;
  proposalId?: string;
  recipientType?: "cliente" | "empresa";
  recipientName?: string;
  onOpenModal?: (
    type: "cliente" | "empresa",
    email: string,
    name: string
  ) => void;
  onOpenWhatsAppModal?: (
    type: "cliente" | "empresa",
    phone: string,
    name: string
  ) => void;
}

function ChannelStatus({
  icon,
  channelName,
  address,
  enviado,
  data_envio,
  erro,
  proposalId,
  recipientType,
  recipientName,
  onOpenModal,
  onOpenWhatsAppModal,
}: ChannelStatusProps) {
  const getStatusInfo = () => {
    // Se não foi enviado ou tem erro, mostra falha
    if (!enviado || erro) {
      return {
        text: erro || "Falha no envio",
        color: "red.500",
        icon: FaInfoCircle,
      };
    }
    // Se foi enviado com sucesso
    return {
      text: `${channelName} enviado com sucesso`,
      color: "green.500",
      icon: FaCheckCircle,
    };
  };

  const statusInfo = getStatusInfo();

  const formatAddress = () => {
    if (channelName === "WhatsApp" && address) {
      return phoneFormatter(address);
    }

    return address || "";
  };

  return (
    <Flex justify="space-between" align="center" py={2} w="100%">
      <HStack spacing={3}>
        <Icon as={icon} boxSize={5} color="gray.600" />
        <VStack align="flex-start" spacing={0}>
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              {channelName}
            </Text>
            {channelName === "E-mail" &&
              proposalId &&
              recipientType &&
              onOpenModal && (
                <Tooltip label="Reenviar email" placement="top">
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<MdRefresh />}
                    colorScheme="blue"
                    onClick={() =>
                      onOpenModal(
                        recipientType,
                        address || "",
                        recipientName || ""
                      )
                    }
                  >
                    Reenviar email
                  </Button>
                </Tooltip>
              )}
            {channelName === "WhatsApp" &&
              proposalId &&
              recipientType &&
              onOpenWhatsAppModal && (
                <Tooltip label="Reenviar WhatsApp" placement="top">
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<MdRefresh />}
                    colorScheme="blue"
                    onClick={() =>
                      onOpenWhatsAppModal(
                        recipientType,
                        address || "",
                        recipientName || ""
                      )
                    }
                  >
                    Reenviar WhatsApp
                  </Button>
                </Tooltip>
              )}
          </HStack>
          <Text fontSize="xs" color="gray.500">
            {formatAddress()}
          </Text>
        </VStack>
      </HStack>

      <VStack align="flex-end" spacing={0}>
        <HStack spacing={1}>
          <Icon as={statusInfo.icon} boxSize={3.5} color={statusInfo.color} />
          <Text fontSize="xs" fontWeight="medium" color={statusInfo.color}>
            {statusInfo.text}
          </Text>
        </HStack>
        {enviado && data_envio && (
          <Text fontSize="xs" color="gray.500" mt={0.5}>
            {format(new Date(data_envio), "dd/MM/yyyy 'às' HH:mm", {
              locale: ptBR,
            })}
          </Text>
        )}
      </VStack>
    </Flex>
  );
}

export function ReportProposalSendEmail({
  proposalId,
}: {
  proposalId: string;
}) {
  const [reportData, setReportData] =
    useState<NotificationReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalEmail, setModalEmail] = useState("");
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [modalPhone, setModalPhone] = useState("");
  const [modalRecipientType, setModalRecipientType] = useState<
    "cliente" | "empresa"
  >("cliente");
  const [modalRecipientName, setModalRecipientName] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isResendingWhatsApp, setIsResendingWhatsApp] = useState(false);
  const toast = useToast();

  const loadReportData = () => {
    setIsLoading(true);
    setErrorMessage(null);
    getReportProposalSendEmail(proposalId)
      .then((data) => {
        setReportData(data);
      })
      .catch((error) => {
        const message = error?.response?.data?.message || error?.message || "";
        setErrorMessage(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadReportData();
  }, [proposalId]);

  const handleOpenModal = (
    type: "cliente" | "empresa",
    email: string,
    name: string
  ) => {
    setModalRecipientType(type);
    setModalEmail(email);
    setModalRecipientName(name);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalEmail("");
    setModalRecipientName("");
  };

  const handleResendEmail = async () => {
    if (!proposalId || !modalEmail.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, informe um endereço de e-mail válido.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsResending(true);
    try {
      if (modalRecipientType === "cliente") {
        await resendProposalEmailEmployee(proposalId, modalEmail.trim());
      } else {
        await resendProposalEmailEmployer(proposalId, modalEmail.trim());
      }

      toast({
        title: "Sucesso",
        description: "E-mail reenviado com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      handleCloseModal();
      loadReportData();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao reenviar e-mail";
      toast({
        title: "Erro",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleOpenWhatsAppModal = (
    type: "cliente" | "empresa",
    phone: string,
    name: string
  ) => {
    setModalRecipientType(type);
    // Extract digits and format phone number
    const phoneNumbers = phone.replace(/\D/g, "");
    setModalPhone(phoneNumbers ? formatPhone(phoneNumbers) : "");
    setModalRecipientName(name);
    setIsWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = () => {
    setIsWhatsAppModalOpen(false);
    setModalPhone("");
    setModalRecipientName("");
  };

  const handleResendWhatsApp = async () => {
    if (!proposalId || !modalPhone.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, informe um número de telefone válido.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const phoneNumbers = modalPhone.replace(/\D/g, "");
    if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
      toast({
        title: "Erro",
        description: "Por favor, informe um número de telefone válido.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const formattedPhone = formatPhone(phoneNumbers);

    setIsResendingWhatsApp(true);
    try {
      await resendProposalWhatsApp(proposalId, formattedPhone);

      toast({
        title: "Sucesso",
        description: "WhatsApp reenviado com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      handleCloseWhatsAppModal();
      loadReportData();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao reenviar WhatsApp";
      toast({
        title: "Erro",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResendingWhatsApp(false);
    }
  };

  return (
    <Box
      flex={{ base: "none", lg: "1" }}
      bg="white"
      borderRadius="lg"
      p={3}
      boxShadow="sm"
      maxH="500px"
      border="1px solid"
      borderColor="gray.200"
      css={{
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
      display="flex"
      flexDirection="column"
      overflow="auto"
      minH={0}
    >
      <HStack spacing={2} mb={3} flexShrink={0}>
        <Icon as={MdMessage} color="secondary" boxSize={4} />
        <Text fontSize="md" fontWeight="bold" color="gray.700">
          Disparo de mensagem
        </Text>
      </HStack>

      {isLoading ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100%"
          flexDirection="column"
          gap={4}
        >
          <Spinner size="xl" color="primary" thickness="4px" speed="0.65s" />
          <Text fontSize="xl" fontWeight="medium">
            Carregando...
          </Text>
        </Flex>
      ) : errorMessage &&
        errorMessage.toLowerCase().includes("notificações não encontradas") ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100%"
          flexDirection="column"
          gap={2}
        >
          <Icon as={FaInfoCircle} boxSize={12} color="gray.400" />
          <Text fontSize="lg" fontWeight="medium" color="gray.600">
            Nenhuma mensagem enviada
          </Text>
          <Text fontSize="sm" color="gray.500">
            Não há notificações registradas para esta proposta
          </Text>
        </Flex>
      ) : errorMessage ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100%"
          flexDirection="column"
          gap={2}
        >
          <Icon as={FaInfoCircle} boxSize={12} color="red.400" />
          <Text fontSize="lg" fontWeight="medium" color="gray.600">
            Erro ao carregar notificações
          </Text>
          <Text fontSize="sm" color="gray.500">
            {errorMessage}
          </Text>
        </Flex>
      ) : !reportData ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100%"
          flexDirection="column"
          gap={2}
        >
          <Icon as={FaInfoCircle} boxSize={12} color="gray.400" />
          <Text fontSize="lg" fontWeight="medium" color="gray.600">
            Nenhuma mensagem enviada
          </Text>
          <Text fontSize="sm" color="gray.500">
            Não há notificações registradas para esta proposta
          </Text>
        </Flex>
      ) : (
        <Box
          flex={1}
          overflowY="auto"
          minH={0}
          css={{
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <VStack align="stretch" spacing={3}>
            <Accordion allowMultiple defaultIndex={[0, 1]}>
              {/* Cliente */}
              <AccordionItem
                border="none"
                borderRadius="md"
                mb={3}
                overflow="hidden"
              >
                <AccordionButton
                  p={3}
                  bg="secondary"
                  color="white"
                  _hover={{ bg: "secondary", opacity: 0.9 }}
                  borderRadius="md"
                >
                  <HStack flex={1} spacing={2}>
                    <Icon as={FaUser} boxSize={4} />
                    <VStack align="flex-start" spacing={0}>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                      >
                        Cliente
                      </Text>
                      <Text fontSize="sm" fontWeight="bold">
                        {reportData.cliente.nome || "Não informado"}
                      </Text>
                      <Text fontSize="xs">
                        CPF:{" "}
                        {reportData.cliente.cpf
                          ? documentFormatter(reportData.cliente.cpf)
                          : "Não informado"}
                      </Text>
                    </VStack>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel
                  pb={3}
                  px={3}
                  pt={3}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderTop="none"
                  borderRadius="0 0 md md"
                >
                  <VStack spacing={0} align="stretch">
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                    >
                      Canais de Disparo
                    </Text>
                    <VStack spacing={0}>
                      <ChannelStatus
                        icon={MdEmail}
                        channelName="E-mail"
                        address={
                          reportData.cliente.canais_disparo.email.endereco
                        }
                        enviado={
                          reportData.cliente.canais_disparo.email.enviado
                        }
                        data_envio={
                          reportData.cliente.canais_disparo.email.data_envio
                        }
                        erro={reportData.cliente.canais_disparo.email.erro}
                        proposalId={proposalId}
                        recipientType="cliente"
                        recipientName={reportData.cliente.nome || undefined}
                        onOpenModal={handleOpenModal}
                      />

                      {reportData.cliente.canais_disparo.chatbot.telefone && (
                        <ChannelStatus
                          icon={MdWhatsapp}
                          channelName="WhatsApp"
                          address={
                            reportData.cliente.canais_disparo.chatbot.telefone
                          }
                          enviado={
                            reportData.cliente.canais_disparo.chatbot.enviado
                          }
                          data_envio={
                            reportData.cliente.canais_disparo.chatbot.data_envio
                          }
                          erro={reportData.cliente.canais_disparo.chatbot.erro}
                          proposalId={proposalId}
                          recipientType="cliente"
                          recipientName={reportData.cliente.nome || undefined}
                          onOpenWhatsAppModal={handleOpenWhatsAppModal}
                        />
                      )}
                      {!reportData.cliente.canais_disparo.email.endereco &&
                        !reportData.cliente.canais_disparo.chatbot.telefone && (
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            textAlign="center"
                            py={4}
                          >
                            Nenhum canal cadastrado
                          </Text>
                        )}
                    </VStack>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              {/* Empresa */}
              <AccordionItem border="none" borderRadius="md" overflow="hidden">
                <AccordionButton
                  p={3}
                  bg="secondary"
                  color="white"
                  _hover={{ bg: "secondary", opacity: 0.9 }}
                  borderRadius="md"
                >
                  <HStack flex={1} spacing={2}>
                    <Icon as={FaBuilding} boxSize={4} />
                    <VStack align="flex-start" spacing={0}>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                      >
                        Empresa
                      </Text>
                      <Text fontSize="sm" fontWeight="bold">
                        {reportData.empresa.nome || "Não informado"}
                      </Text>
                      <Text fontSize="xs">
                        CNPJ:{" "}
                        {reportData.empresa.cnpj
                          ? documentFormatter(reportData.empresa.cnpj)
                          : "Não informado"}
                      </Text>
                    </VStack>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel
                  pb={3}
                  px={3}
                  pt={3}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderTop="none"
                  borderRadius="0 0 md md"
                >
                  <VStack spacing={0} align="stretch">
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                    >
                      Canais de Disparo
                    </Text>
                    <VStack spacing={0}>
                      <ChannelStatus
                        icon={MdEmail}
                        channelName="E-mail"
                        address={
                          reportData.empresa.canais_disparo.email.endereco
                        }
                        enviado={
                          reportData.empresa.canais_disparo.email.enviado
                        }
                        data_envio={
                          reportData.empresa.canais_disparo.email.data_envio
                        }
                        erro={reportData.empresa.canais_disparo.email.erro}
                        proposalId={proposalId}
                        recipientType="empresa"
                        recipientName={reportData.empresa.nome || undefined}
                        onOpenModal={handleOpenModal}
                      />

                      {reportData.empresa.canais_disparo.chatbot.telefone && (
                        <ChannelStatus
                          icon={MdWhatsapp}
                          channelName="WhatsApp"
                          address={
                            reportData.empresa.canais_disparo.chatbot.telefone
                          }
                          enviado={
                            reportData.empresa.canais_disparo.chatbot.enviado
                          }
                          data_envio={
                            reportData.empresa.canais_disparo.chatbot.data_envio
                          }
                          erro={reportData.empresa.canais_disparo.chatbot.erro}
                          proposalId={proposalId}
                          recipientType="empresa"
                          recipientName={reportData.empresa.nome || undefined}
                          onOpenWhatsAppModal={handleOpenWhatsAppModal}
                        />
                      )}
                      {!reportData.empresa.canais_disparo.email.endereco &&
                        !reportData.empresa.canais_disparo.chatbot.telefone && (
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            textAlign="center"
                            py={4}
                          >
                            Nenhum canal cadastrado
                          </Text>
                        )}
                    </VStack>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </VStack>
        </Box>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent borderRadius="xl" p={4}>
          <ModalHeader display="flex" alignItems="center" gap={2}>
            <Icon as={MdEmail} boxSize={6} color="blue.600" />
            Reenviar E-mail
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text color="gray.600" mb={6}>
              {modalEmail ? (
                <>
                  Reenviar notificação para{" "}
                  <Text as="span" fontWeight="bold">
                    {modalRecipientName ||
                      (modalRecipientType === "cliente"
                        ? reportData?.cliente.nome
                        : reportData?.empresa.nome) ||
                      "destinatário"}
                  </Text>
                  . Você pode editar o endereço de e-mail antes de enviar.
                </>
              ) : (
                <>
                  Enviar notificação para{" "}
                  <Text as="span" fontWeight="bold">
                    {modalRecipientName ||
                      (modalRecipientType === "cliente"
                        ? reportData?.cliente.nome
                        : reportData?.empresa.nome) ||
                      "destinatário"}
                  </Text>
                  . Informe o endereço de e-mail para envio.
                </>
              )}
            </Text>

            <FormControl>
              <FormLabel fontWeight="bold">Endereço de E-mail</FormLabel>

              <Input
                placeholder="exemplo@email.com"
                size="lg"
                borderRadius="lg"
                focusBorderColor="blue.600"
                value={modalEmail}
                onChange={(e) => setModalEmail(e.target.value)}
                type="email"
              />
            </FormControl>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button
              variant="outline"
              onClick={handleCloseModal}
              isDisabled={isResending}
            >
              Cancelar
            </Button>

            <Button
              colorScheme="blue"
              leftIcon={<MdEmail />}
              onClick={handleResendEmail}
              isLoading={isResending}
              loadingText="Enviando..."
            >
              Reenviar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isWhatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent borderRadius="xl" p={4}>
          <ModalHeader display="flex" alignItems="center" gap={2}>
            <Icon as={MdWhatsapp} boxSize={6} color="green.600" />
            Reenviar WhatsApp
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text color="gray.600" mb={6}>
              {modalPhone ? (
                <>
                  Reenviar notificação WhatsApp para{" "}
                  <Text as="span" fontWeight="bold">
                    {modalRecipientName ||
                      (modalRecipientType === "cliente"
                        ? reportData?.cliente.nome
                        : reportData?.empresa.nome) ||
                      "destinatário"}
                  </Text>
                  . Você pode editar o número de telefone antes de enviar.
                </>
              ) : (
                <>
                  Enviar notificação WhatsApp para{" "}
                  <Text as="span" fontWeight="bold">
                    {modalRecipientName ||
                      (modalRecipientType === "cliente"
                        ? reportData?.cliente.nome
                        : reportData?.empresa.nome) ||
                      "destinatário"}
                  </Text>
                  . Informe o número de telefone para envio.
                </>
              )}
            </Text>

            <FormControl>
              <FormLabel fontWeight="bold">Número de Telefone</FormLabel>

              <Input
                placeholder="(11) 99999-9999"
                size="lg"
                borderRadius="lg"
                focusBorderColor="green.600"
                value={modalPhone}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow digits and format as user types
                  const numbers = value.replace(/\D/g, "");
                  if (numbers.length <= 11) {
                    setModalPhone(formatPhone(numbers));
                  }
                }}
                type="tel"
                maxLength={15}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button
              variant="outline"
              onClick={handleCloseWhatsAppModal}
              isDisabled={isResendingWhatsApp}
            >
              Cancelar
            </Button>

            <Button
              colorScheme="blue"
              leftIcon={<MdWhatsapp />}
              onClick={handleResendWhatsApp}
              isLoading={isResendingWhatsApp}
              loadingText="Enviando..."
            >
              Reenviar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
